import { useState, useCallback, useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth';
import { usePregnancyStore } from '@/store/pregnancyStore';
import { isValidUUID } from '@/lib/utils';
import type {
  Appointment,
  AppointmentInput,
  AppointmentType,
  RecurrenceConfig,
  RecurrencePattern,
  SeriesScope,
} from '../types';
import { RECURRENCE_LIMITS } from '../types';
import { PREDEFINED_CHECKUPS, calculateDateForSSW } from '../data/predefinedCheckups';

type ContextType = 'pregnancy' | 'baby' | 'family';

const RECURRENCE_HARD_CAP = 52;
const REALTIME_DEBOUNCE_MS = 300;

export interface BulkAppointmentInput {
  title: string;
  description: string;
  appointment_date: string;
  appointment_time?: string;
  appointment_type: AppointmentType;
  ssw_week?: number;
  is_all_day: boolean;
}

interface UseAppointmentsReturn {
  appointments: Appointment[];
  isLoading: boolean;
  error: string | null;
  addAppointment: (input: AppointmentInput) => Promise<boolean>;
  addMultipleAppointments: (inputs: BulkAppointmentInput[]) => Promise<boolean>;
  /**
   * Update an appointment. If the row is part of a series, `scope` controls
   * whether the update applies only to this occurrence (`'single'`, default),
   * all following occurrences (`'following'`) or the whole series (`'all'`).
   *
   * Scope `'single'` on a series row also detaches the row from the group:
   * recurrence_group_id and recurrence_pattern are cleared, so future
   * series-wide operations no longer affect it. This matches Google/Apple
   * Calendar behaviour.
   */
  updateAppointment: (
    id: string,
    input: Partial<AppointmentInput>,
    scope?: SeriesScope
  ) => Promise<boolean>;
  deleteAppointment: (id: string, scope?: SeriesScope) => Promise<boolean>;
  deleteAllAppointments: () => Promise<boolean>;
  addPredefinedCheckups: () => Promise<boolean>;
  hasPredefinedCheckups: boolean;
  refetch: () => Promise<void>;
}

function addMonthsClamped(date: Date, months: number): Date {
  const startDay = date.getDate();
  const result = new Date(date);
  result.setDate(1);
  result.setMonth(result.getMonth() + months);
  const daysInTargetMonth = new Date(
    result.getFullYear(),
    result.getMonth() + 1,
    0
  ).getDate();
  result.setDate(Math.min(startDay, daysInTargetMonth));
  return result;
}

function formatLocalDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function parseLocalDate(iso: string): Date {
  const [y, m, d] = iso.split('-').map((n) => parseInt(n, 10));
  return new Date(y, m - 1, d);
}

interface ExpansionContext {
  title: string;
  description: string | null;
  appointment_time: string | null;
  is_all_day: boolean;
  appointment_type: AppointmentType;
  reminder: AppointmentInput['reminder'];
  pregnancy_id: string;
  created_by: string;
  context_type: ContextType;
}

function expandRecurrence(
  firstDate: string,
  recurrence: RecurrenceConfig,
  ctx: ExpansionContext,
  groupId: string
): Record<string, unknown>[] {
  const start = parseLocalDate(firstDate);

  // Determine hard cap relative to pattern and the absolute 1-year limit
  const maxByPattern = RECURRENCE_LIMITS[recurrence.pattern];
  const maxAbsolute = RECURRENCE_HARD_CAP;
  const patternCap = Math.min(maxByPattern, maxAbsolute);

  let targetCount: number;
  if (recurrence.endMode === 'count') {
    const c = Math.max(1, Math.floor(recurrence.count ?? 1));
    targetCount = Math.min(c, patternCap);
  } else {
    // endMode === 'until'
    if (!recurrence.endDate) return [];
    const end = parseLocalDate(recurrence.endDate);
    // 1-year hard cap from start
    const oneYearFromStart = new Date(start);
    oneYearFromStart.setFullYear(oneYearFromStart.getFullYear() + 1);
    const effectiveEnd = end > oneYearFromStart ? oneYearFromStart : end;

    targetCount = 0;
    for (let i = 0; i < patternCap; i++) {
      const candidate = nextOccurrence(start, recurrence.pattern, i);
      if (candidate > effectiveEnd) break;
      targetCount = i + 1;
    }
  }

  const rows: Record<string, unknown>[] = [];
  for (let i = 0; i < targetCount; i++) {
    const occurrence = nextOccurrence(start, recurrence.pattern, i);
    rows.push({
      pregnancy_id: ctx.pregnancy_id,
      created_by: ctx.created_by,
      title: ctx.title,
      description: ctx.description,
      appointment_date: formatLocalDate(occurrence),
      appointment_time: ctx.is_all_day ? null : ctx.appointment_time,
      is_all_day: ctx.is_all_day,
      appointment_type: ctx.appointment_type,
      reminder: ctx.reminder,
      is_predefined: false,
      context_type: ctx.context_type,
      recurrence_group_id: groupId,
      recurrence_pattern: recurrence.pattern,
    });
  }
  return rows;
}

function nextOccurrence(start: Date, pattern: RecurrencePattern, stepIndex: number): Date {
  if (pattern === 'weekly') {
    const d = new Date(start);
    d.setDate(d.getDate() + 7 * stepIndex);
    return d;
  }
  if (pattern === 'biweekly') {
    const d = new Date(start);
    d.setDate(d.getDate() + 14 * stepIndex);
    return d;
  }
  // monthly — clamp at month end
  return addMonthsClamped(start, stepIndex);
}

export function useAppointments(): UseAppointmentsReturn {
  const { user } = useAuth();
  const { currentPregnancy, lastCompletedPregnancy, appMode } = usePregnancyStore();
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isBabyMode = appMode === 'baby' && lastCompletedPregnancy?.completion_type === 'birth';
  const rawPregnancyId = currentPregnancy?.id || (isBabyMode ? lastCompletedPregnancy?.id : null);
  const pregnancyId = isValidUUID(rawPregnancyId) ? rawPregnancyId : null;
  const contextType: ContextType = isBabyMode ? 'baby' : 'pregnancy';

  const fetchAppointments = useCallback(async () => {
    if (!pregnancyId) {
      setAppointments([]);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('user_appointments')
        .select('*')
        .eq('pregnancy_id', pregnancyId)
        .order('appointment_date', { ascending: true })
        .order('appointment_time', { ascending: true, nullsFirst: false });

      if (fetchError) throw fetchError;
      setAppointments(data || []);
    } catch (err) {
      console.error('Error fetching appointments:', err);
      setError('Fehler beim Laden der Termine');
    } finally {
      setIsLoading(false);
    }
  }, [pregnancyId]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  // Realtime subscription with debounced refetch — series operations emit N
  // row events; coalesce them into a single fetch.
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!pregnancyId) return;

    const scheduleFetch = () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(() => {
        fetchAppointments();
      }, REALTIME_DEBOUNCE_MS);
    };

    const channel = supabase
      .channel(`appointments:${pregnancyId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_appointments',
          filter: `pregnancy_id=eq.${pregnancyId}`,
        },
        () => scheduleFetch()
      )
      .subscribe();

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      supabase.removeChannel(channel);
    };
  }, [pregnancyId, fetchAppointments]);

  const addAppointment = useCallback(
    async (input: AppointmentInput): Promise<boolean> => {
      if (!user?.id || !pregnancyId) return false;

      try {
        if (input.recurrence) {
          const groupId =
            typeof crypto !== 'undefined' && 'randomUUID' in crypto
              ? crypto.randomUUID()
              : undefined;
          if (!groupId) {
            setError('UUID-Erzeugung nicht unterstützt');
            return false;
          }

          const rows = expandRecurrence(
            input.appointment_date,
            input.recurrence,
            {
              title: input.title,
              description: input.description || null,
              appointment_time: input.is_all_day ? null : input.appointment_time ?? null,
              is_all_day: input.is_all_day,
              appointment_type: input.appointment_type,
              reminder: input.reminder,
              pregnancy_id: pregnancyId,
              created_by: user.id,
              context_type: contextType,
            },
            groupId
          );

          if (rows.length === 0) {
            setError('Keine gültigen Wiederholungen');
            return false;
          }
          if (rows.length > RECURRENCE_HARD_CAP) {
            setError(`Serien sind auf max. ${RECURRENCE_HARD_CAP} Termine begrenzt`);
            return false;
          }

          const { error: insertError } = await supabase
            .from('user_appointments')
            .insert(rows);

          if (insertError) throw insertError;
          return true;
        }

        const { error: insertError } = await supabase.from('user_appointments').insert({
          pregnancy_id: pregnancyId,
          created_by: user.id,
          title: input.title,
          description: input.description || null,
          appointment_date: input.appointment_date,
          appointment_time: input.is_all_day ? null : input.appointment_time,
          is_all_day: input.is_all_day,
          appointment_type: input.appointment_type,
          reminder: input.reminder,
          is_predefined: false,
          context_type: contextType,
        });

        if (insertError) throw insertError;
        return true;
      } catch (err) {
        console.error('Error adding appointment:', err);
        setError('Fehler beim Erstellen des Termins');
        return false;
      }
    },
    [user?.id, pregnancyId, contextType]
  );

  const addMultipleAppointments = useCallback(
    async (inputs: BulkAppointmentInput[]): Promise<boolean> => {
      if (!user?.id || !pregnancyId || inputs.length === 0) return false;

      try {
        const appointmentsToInsert = inputs.map((input) => ({
          pregnancy_id: pregnancyId,
          created_by: user.id,
          title: input.title,
          description: input.description || null,
          appointment_date: input.appointment_date,
          appointment_time: input.appointment_time || null,
          is_all_day: input.is_all_day,
          appointment_type: input.appointment_type,
          reminder: 'none' as const,
          is_predefined: !!input.ssw_week,
          ssw_week: input.ssw_week || null,
          context_type: contextType,
        }));

        const { error: insertError } = await supabase
          .from('user_appointments')
          .insert(appointmentsToInsert);

        if (insertError) throw insertError;
        return true;
      } catch (err) {
        console.error('Error adding multiple appointments:', err);
        setError('Fehler beim Hinzufügen der Termine');
        return false;
      }
    },
    [user?.id, pregnancyId, contextType]
  );

  /**
   * Update an appointment. Behaviour per scope:
   * - 'single' (default): update only this row. If the row belonged to a
   *   series, detach it by clearing recurrence_group_id and recurrence_pattern.
   * - 'following': update all rows with the same recurrence_group_id and
   *   appointment_date >= this row's date. If the input contains a new
   *   `recurrence` config, the affected portion is deleted and regenerated.
   * - 'all': same as 'following' but for every row of the series.
   */
  const updateAppointment = useCallback(
    async (
      id: string,
      input: Partial<AppointmentInput>,
      scope: SeriesScope = 'single'
    ): Promise<boolean> => {
      if (!user?.id || !pregnancyId) return false;

      try {
        const current = appointments.find((a) => a.id === id);
        const isSeriesRow = !!current?.recurrence_group_id;

        // Build basic update payload (common to every scope)
        const baseUpdate: Record<string, unknown> = {
          updated_at: new Date().toISOString(),
        };
        if (input.title !== undefined) baseUpdate.title = input.title;
        if (input.description !== undefined)
          baseUpdate.description = input.description || null;
        if (input.appointment_date !== undefined)
          baseUpdate.appointment_date = input.appointment_date;
        if (input.is_all_day !== undefined) {
          baseUpdate.is_all_day = input.is_all_day;
          if (input.is_all_day) baseUpdate.appointment_time = null;
        }
        if (input.appointment_time !== undefined && !input.is_all_day) {
          baseUpdate.appointment_time = input.appointment_time;
        }
        if (input.appointment_type !== undefined)
          baseUpdate.appointment_type = input.appointment_type;
        if (input.reminder !== undefined) baseUpdate.reminder = input.reminder;

        // Single-row path: simple update, detach from series when applicable
        if (scope === 'single' || !isSeriesRow) {
          const updateData = { ...baseUpdate };
          if (isSeriesRow) {
            updateData.recurrence_group_id = null;
            updateData.recurrence_pattern = null;
          }
          const { error: updateError } = await supabase
            .from('user_appointments')
            .update(updateData)
            .eq('id', id);
          if (updateError) throw updateError;
          return true;
        }

        // Scope 'following' | 'all' on a series row
        const groupId = current!.recurrence_group_id!;
        const fromDate = scope === 'following' ? current!.appointment_date : null;

        // If the caller passes a new recurrence config, rebuild affected rows
        if (input.recurrence) {
          // Determine anchor for expansion: first affected row's date
          const affected = appointments.filter(
            (a) =>
              a.recurrence_group_id === groupId &&
              (fromDate === null || a.appointment_date >= fromDate)
          );
          if (affected.length === 0) return true;
          const anchorDate = affected
            .map((a) => a.appointment_date)
            .sort()[0];

          // Delete affected rows
          let deleteQuery = supabase
            .from('user_appointments')
            .delete()
            .eq('recurrence_group_id', groupId);
          if (fromDate !== null) deleteQuery = deleteQuery.gte('appointment_date', fromDate);
          const { error: delError } = await deleteQuery;
          if (delError) throw delError;

          // Expand and insert fresh rows, reusing the same group_id
          const exampleRow = affected[0];
          const rows = expandRecurrence(
            input.appointment_date ?? anchorDate,
            input.recurrence,
            {
              title: input.title ?? exampleRow.title,
              description:
                input.description !== undefined
                  ? input.description || null
                  : exampleRow.description ?? null,
              appointment_time:
                input.is_all_day ?? exampleRow.is_all_day
                  ? null
                  : input.appointment_time ?? exampleRow.appointment_time ?? null,
              is_all_day: input.is_all_day ?? exampleRow.is_all_day,
              appointment_type:
                input.appointment_type ?? exampleRow.appointment_type,
              reminder: input.reminder ?? exampleRow.reminder,
              pregnancy_id: pregnancyId,
              created_by: user.id,
              context_type: contextType,
            },
            groupId
          );

          if (rows.length > 0) {
            const { error: insertError } = await supabase
              .from('user_appointments')
              .insert(rows);
            if (insertError) throw insertError;
          }
          return true;
        }

        // Pure field update on existing series rows
        let updateQuery = supabase
          .from('user_appointments')
          .update(baseUpdate)
          .eq('recurrence_group_id', groupId);
        if (fromDate !== null) updateQuery = updateQuery.gte('appointment_date', fromDate);
        const { error: updateError } = await updateQuery;
        if (updateError) throw updateError;
        return true;
      } catch (err) {
        console.error('Error updating appointment:', err);
        setError('Fehler beim Aktualisieren des Termins');
        return false;
      }
    },
    [appointments, user?.id, pregnancyId, contextType]
  );

  const deleteAppointment = useCallback(
    async (id: string, scope: SeriesScope = 'single'): Promise<boolean> => {
      try {
        const current = appointments.find((a) => a.id === id);
        const groupId = current?.recurrence_group_id ?? null;

        if (scope === 'single' || !groupId) {
          const { error: deleteError } = await supabase
            .from('user_appointments')
            .delete()
            .eq('id', id);
          if (deleteError) throw deleteError;
          return true;
        }

        let delQuery = supabase
          .from('user_appointments')
          .delete()
          .eq('recurrence_group_id', groupId);
        if (scope === 'following') {
          delQuery = delQuery.gte('appointment_date', current!.appointment_date);
        }
        const { error: deleteError } = await delQuery;
        if (deleteError) throw deleteError;
        return true;
      } catch (err) {
        console.error('Error deleting appointment:', err);
        setError('Fehler beim Löschen des Termins');
        return false;
      }
    },
    [appointments]
  );

  const deleteAllAppointments = useCallback(async (): Promise<boolean> => {
    if (!pregnancyId) return false;

    try {
      const { error: deleteError } = await supabase
        .from('user_appointments')
        .delete()
        .eq('pregnancy_id', pregnancyId);

      if (deleteError) throw deleteError;
      return true;
    } catch (err) {
      console.error('Error deleting all appointments:', err);
      setError('Fehler beim Löschen aller Termine');
      return false;
    }
  }, [pregnancyId]);

  const addPredefinedCheckups = useCallback(async (): Promise<boolean> => {
    if (!user?.id || !pregnancyId || !currentPregnancy?.expected_due_date) {
      return false;
    }

    try {
      const dueDate = new Date(currentPregnancy.expected_due_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const futureCheckups = PREDEFINED_CHECKUPS.filter((checkup) => {
        const checkupDate = calculateDateForSSW(dueDate, checkup.ssw_week);
        return checkupDate >= today;
      });

      if (futureCheckups.length === 0) {
        return true;
      }

      const appointmentsToInsert = futureCheckups.map((checkup) => {
        const appointmentDate = calculateDateForSSW(dueDate, checkup.ssw_week);
        return {
          pregnancy_id: pregnancyId,
          created_by: user.id,
          title: checkup.title,
          description: checkup.description,
          appointment_date: appointmentDate.toISOString().split('T')[0],
          appointment_time: null,
          is_all_day: true,
          appointment_type: checkup.appointment_type,
          reminder: 'none' as const,
          is_predefined: true,
          ssw_week: checkup.ssw_week,
          context_type: contextType,
        };
      });

      const { error: insertError } = await supabase
        .from('user_appointments')
        .insert(appointmentsToInsert);

      if (insertError) throw insertError;
      return true;
    } catch (err) {
      console.error('Error adding predefined checkups:', err);
      setError('Fehler beim Hinzufügen der Vorsorgeuntersuchungen');
      return false;
    }
  }, [user?.id, pregnancyId, currentPregnancy?.expected_due_date, contextType]);

  const hasPredefinedCheckups = appointments.some((apt) => apt.is_predefined);

  return {
    appointments,
    isLoading,
    error,
    addAppointment,
    addMultipleAppointments,
    updateAppointment,
    deleteAppointment,
    deleteAllAppointments,
    addPredefinedCheckups,
    hasPredefinedCheckups,
    refetch: fetchAppointments,
  };
}
